import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Search, Plus, Play, MoreHorizontal, Edit, Trash2, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog'
import { useTestSets } from '@/hooks/useTestSets'
import { EditTestSetModal } from './EditTestSetModal'
import type { TestSetWithStats } from '@shared/types/evaluation'
import { PROTECTED_ROUTES } from '@/constants/routes'

export const TestSetList: React.FC = () => {
  const { testSets, loading, error, deleteTestSet } = useTestSets()
  const [searchQuery, setSearchQuery] = useState('')
  const [editingTestSet, setEditingTestSet] = useState<TestSetWithStats | null>(null)

  // Ensure testSets is always an array
  const safeTestSets = Array.isArray(testSets) ? testSets : []

  const filteredTestSets = safeTestSets.filter(testSet =>
    testSet.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (testSet.description?.toLowerCase().includes(searchQuery.toLowerCase()))
  )

  const handleDelete = async (testSetId: string) => {
    try {
      await deleteTestSet(testSetId)
    } catch (err) {
      // Error handling is done in the hook
    }
  }

  const getStatusBadge = (status?: string) => {
    if (!status) return null
    
    const variants = {
      COMPLETED: 'default' as const,
      RUNNING: 'secondary' as const,
      FAILED: 'destructive' as const,
      CANCELLED: 'outline' as const
    }
    
    return (
      <Badge variant={variants[status as keyof typeof variants] || 'outline'}>
        {status}
      </Badge>
    )
  }

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-4 bg-muted rounded w-1/3"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </CardHeader>
            <CardContent>
              <div className="h-3 bg-muted rounded w-full"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <div className="flex justify-center mb-4">
              <AlertTriangle className="h-12 w-12 text-destructive" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Unable to load evaluations</h3>
            <p className="text-muted-foreground mb-4">
              The evaluation system may not be available. Please ensure the backend server is running.
            </p>
            <p className="text-sm text-muted-foreground text-left bg-muted p-3 rounded">
              <strong>Error:</strong> {error}
            </p>
            <div className="mt-4">
              <Button 
                variant="outline" 
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search test sets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Test Sets */}
      {filteredTestSets.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center mx-auto mb-4">
                <Plus className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No test sets found</h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery ? 'No test sets match your search.' : 'Create your first test set to get started with evaluations.'}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredTestSets.map((testSet) => (
            <Card key={testSet.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <CardTitle className="text-lg">
                        <Link 
                          to={`${PROTECTED_ROUTES.EVALUATIONS}/${testSet.id}`}
                          className="hover:underline"
                        >
                          {testSet.name}
                        </Link>
                      </CardTitle>
                      {testSet.latestRun && getStatusBadge(testSet.latestRun.status)}
                    </div>
                    {testSet.description && (
                      <CardDescription>{testSet.description}</CardDescription>
                    )}
                  </div>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setEditingTestSet(testSet)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <DropdownMenuItem 
                            onSelect={(e) => e.preventDefault()}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Test Set</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{testSet.name}"? This action cannot be undone.
                              All test cases and evaluation results will be permanently deleted.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleDelete(testSet.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6 text-sm text-muted-foreground">
                    <span>{testSet._count.testCases} test cases</span>
                    <span>{testSet._count.evaluationRuns} runs</span>
                    <span>Model: {testSet.model}</span>
                    {testSet.latestRun?.averageScore !== undefined && (
                      <span>Score: {testSet.latestRun.averageScore.toFixed(1)}</span>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Link to={`${PROTECTED_ROUTES.EVALUATIONS}/${testSet.id}/results`}>
                      <Button variant="outline" size="sm">
                        View Results
                      </Button>
                    </Link>
                    <Link to={`${PROTECTED_ROUTES.EVALUATIONS}/${testSet.id}`}>
                      <Button size="sm" className="gap-2">
                        <Play className="h-4 w-4" />
                        Run
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {editingTestSet && (
        <EditTestSetModal
          testSet={editingTestSet}
          isOpen={true}
          onClose={() => setEditingTestSet(null)}
        />
      )}
    </div>
  )
}